import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star, Loader2, Copy, ExternalLink, MessageSquareWarning, Sparkles, Check, MessageSquareHeart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIP_BTN = "rounded-full border px-3 py-1.5 text-sm font-medium transition-all";

export default function ReviewLanding({ kind = "request" }) {
  const { token } = useParams();
  const base = `${API}/${kind === "site" ? "review" : "r"}/${token}`;
  const canTrackReviewed = kind === "request";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [selected, setSelected] = useState([]);
  const [words, setWords] = useState("");
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [showConcern, setShowConcern] = useState(false);
  const [concern, setConcern] = useState("");
  const [concernSent, setConcernSent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(base).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  const toggle = (c) => setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const genDraft = async () => {
    setDrafting(true);
    try {
      const { data: d } = await axios.post(`${base}/draft`, { chips: selected, text: words });
      if (d.draft) setDraft(d.draft);
      if (d.degraded) toast.message("Here's a starting point from your notes — edit it to sound like you.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add a little about your experience first.");
    } finally {
      setDrafting(false);
    }
  };

  const copyAndGo = async () => {
    if (draft) {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      toast.success("Review copied — paste it on Google");
      setTimeout(() => setCopied(false), 2500);
    }
    if (canTrackReviewed) axios.post(`${base}/reviewed`).catch(() => {});
    window.open(data.google_review_url, "_blank");
  };

  const sendConcern = async () => {
    try {
      await axios.post(`${base}/concern`, { message: concern });
      setConcernSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Please add a short message.");
    }
  };

  if (err) return <Centered>This review link is no longer valid.</Centered>;
  if (!data) return <Centered><Loader2 className="h-6 w-6 animate-spin" /></Centered>;

  const primary = data.branding?.primary_color || "#102542";
  const accent = data.branding?.accent_color || "#3ab795";

  return (
    <div className="min-h-screen bg-slate-50 px-5 py-10 text-slate-900" data-testid="review-landing">
      <div className="mx-auto max-w-md">
        {data.branding?.logo && <img src={data.branding.logo} alt="" className="mx-auto mb-5 h-12 object-contain" />}
        <div className="rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-100">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: `${accent}1a`, color: accent }}>
            <MessageSquareHeart className="h-6 w-6" />
          </div>
          <h1 className="text-center text-2xl font-bold" style={{ color: primary }} data-testid="landing-business">
            {data.first_name ? `Thanks, ${data.first_name}!` : "Thanks!"}
          </h1>
          <p className="mt-1 text-center text-sm text-slate-500">
            How was your experience with <span className="font-semibold text-slate-700">{data.business}</span>?
          </p>

          <a
            href={data.google_review_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => { if (canTrackReviewed) axios.post(`${base}/reviewed`).catch(() => {}); }}
            data-testid="landing-google-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-4 text-base font-bold text-white shadow-lg transition-transform active:scale-[0.98]"
            style={{ background: accent }}
          >
            <Star className="h-5 w-5 fill-current" /> Leave a Google review
          </a>

          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: primary }}>
              <Sparkles className="h-4 w-4" /> Not sure what to say? We'll help.
            </div>
            <p className="mt-1 text-xs text-slate-500">Tap what stood out, add your own words, and we'll draft it — you stay in control and post it yourself.</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="landing-chips">
              {(data.chips || []).map((c) => {
                const on = selected.includes(c);
                return (
                  <button
                    key={c}
                    onClick={() => toggle(c)}
                    data-testid={`chip-${c.replace(/\s/g, "-").toLowerCase()}`}
                    className={CHIP_BTN}
                    style={on ? { background: accent, color: "#fff", borderColor: accent } : { borderColor: "#cbd5e1", color: "#475569" }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <textarea
              value={words}
              onChange={(e) => setWords(e.target.value)}
              placeholder="In your own words (optional)…"
              data-testid="landing-words"
              className="mt-3 w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm text-slate-900 outline-none focus:border-slate-400"
              rows={2}
            />
            <button
              onClick={genDraft}
              disabled={drafting}
              data-testid="landing-generate-draft"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors"
              style={{ borderColor: primary, color: primary }}
            >
              {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Help me write it
            </button>

            {draft && (
              <div className="mt-3" data-testid="landing-draft-block">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  data-testid="landing-draft"
                  className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm text-slate-900 outline-none focus:border-slate-400"
                  rows={4}
                />
                <button
                  onClick={copyAndGo}
                  data-testid="landing-copy-go"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-bold text-white"
                  style={{ background: primary }}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy &amp; open Google
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4 text-center">
            {!showConcern && !concernSent && (
              <button
                onClick={() => setShowConcern(true)}
                data-testid="landing-concern-toggle"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                <MessageSquareWarning className="h-4 w-4" /> Have a concern? Message the owner directly
              </button>
            )}
            {showConcern && !concernSent && (
              <div className="text-left" data-testid="landing-concern-form">
                <textarea
                  value={concern}
                  onChange={(e) => setConcern(e.target.value)}
                  placeholder="Tell the owner what went wrong — this goes straight to them, privately."
                  className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm text-slate-900 outline-none focus:border-slate-400"
                  rows={3}
                  data-testid="landing-concern-input"
                />
                <button
                  onClick={sendConcern}
                  data-testid="landing-concern-send"
                  className="mt-2 w-full rounded-lg bg-slate-800 px-3 py-2.5 text-sm font-semibold text-white"
                >
                  Send to owner
                </button>
              </div>
            )}
            {concernSent && (
              <p className="text-sm font-medium text-emerald-600" data-testid="landing-concern-done">
                Thank you — the owner has been notified and will reach out.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">{children}</div>;
}
